import React, { useState, useCallback } from 'react'
import PropTypes from 'prop-types'

import injectStyles from 'react-jss'

import { ButtonWithLabel } from 'Components/Buttons/ButtonWithLabel'
import CloseIcon from 'Assets/Images/icons/close'
import CalendarIcon from 'Assets/Images/icons/view'
import CheckedIcon from 'Assets/Images/icons/checked'
import { Row, Col } from 'react-bootstrap'
import SubmitButton from 'Components/Form/SubmitButton'
import { Input } from 'Components/Form/Input'
import { SelectDashboard } from 'Components/Form/SelectDashboard'
import { Checkbox } from 'Components/Form/Checkbox'
import { authorization } from '../../../../../Module/Auth/Auth'
import {
  AMBASSADOR_STATUS_KEY,
  AMBASSADOR_STATUSES,
  BLOCK_AMBASSADOR_REASON_OPTIONS,
  BLOCK_AMBASSADOR_REASON_OTHER,
} from 'Reducers/Ambassador/const'

const styles = {
  blockingDisclaimer: { margin: '16px 0' },
  checkboxWrapper: { display: 'flex' },
  checkboxLabel: { marginLeft: 16 },
}

const AmbassadorStatusChange = ({
  ambassadorStatus,
  onChange,
  onFadeChange,
  fade,
  disabled,
  classes,
}) => {
  const [blockReasonOption, setBlockReasonOption] = useState('')
  const [blockCustomText, setBlockCustomText] = useState('')
  const [showDisableForm, setShowDisableForm] = useState(false)
  const [forceUnbook, setForceUnbook] = useState(false)

  const handleBlockFormSubmit = useCallback(
    e => {
      const reason =
        blockReasonOption === BLOCK_AMBASSADOR_REASON_OTHER
          ? blockCustomText
          : blockReasonOption

      onChange({
        status: AMBASSADOR_STATUSES.blocked,
        blockedReason: reason,
        unbookMissions: forceUnbook,
      })

      //set default state after submit
      setShowDisableForm(false)
      setBlockReasonOption('')
      setBlockCustomText('')
      setForceUnbook(false)
    },
    [
      blockCustomText,
      blockReasonOption,
      forceUnbook,
      onChange,
      setShowDisableForm,
      setBlockReasonOption,
      setBlockCustomText,
      setForceUnbook,
    ],
  )

  const handleBlockReasonOptionChange = useCallback(
    value => setBlockReasonOption(value),
    [setBlockReasonOption],
  )
  const handleBlockCustomTextChange = useCallback(
    e => setBlockCustomText(e.target.value),
    [setBlockCustomText],
  )

  const handleApprove = useCallback(
    () => onChange({ status: AMBASSADOR_STATUSES.active }),
    [onChange],
  )

  const handleShowDisableForm = () => setShowDisableForm(true)

  const toggleForceUnbook = () => setForceUnbook(!forceUnbook)

  const options = BLOCK_AMBASSADOR_REASON_OPTIONS.map(option => ({
    label: option,
    value: option,
  }))

  const handleUnbookMissions = useCallback(
    () => onChange({ unbookMissions: true }),
    [onChange],
  )

  const isSubmitBlockedFormDisabled =
    blockReasonOption.length === 0 ||
    (blockReasonOption === BLOCK_AMBASSADOR_REASON_OTHER &&
      blockCustomText.length === 0)

  const buttons = []

  if (
    [
      AMBASSADOR_STATUS_KEY.BLOCKED,
      AMBASSADOR_STATUS_KEY.TERMINATING,
      AMBASSADOR_STATUS_KEY.PENDING,
      AMBASSADOR_STATUS_KEY.WAITING_FOR_REVIEW,
    ].includes(ambassadorStatus)
  ) {
    buttons.push(
      <ButtonWithLabel
        label="Approve"
        icon={<CheckedIcon />}
        color={'red'}
        onClickHandler={handleApprove}
        key={AMBASSADOR_STATUSES.active}
        disabled={disabled}
      />,
    )
  }

  if (
    [
      AMBASSADOR_STATUS_KEY.ACTIVE,
      AMBASSADOR_STATUS_KEY.PENDING,
      AMBASSADOR_STATUS_KEY.WAITING_FOR_REVIEW,
    ].includes(ambassadorStatus)
  ) {
    const label =
      ambassadorStatus === AMBASSADOR_STATUS_KEY.ACTIVE ? 'Block' : 'Refuse'
    buttons.push(
      <ButtonWithLabel
        label={label}
        icon={<CloseIcon />}
        disabled={disabled || showDisableForm}
        onClickHandler={handleShowDisableForm}
        key={AMBASSADOR_STATUSES.blocked}
      />,
    )
  }

  if (
    [
      AMBASSADOR_STATUS_KEY.BLOCKED,
      AMBASSADOR_STATUS_KEY.ACTIVE,
      AMBASSADOR_STATUS_KEY.TERMINATING,
    ].includes(ambassadorStatus)
  ) {
    buttons.push(
      <ButtonWithLabel
        label="Unbook all missions"
        icon={<CloseIcon />}
        onClickHandler={handleUnbookMissions}
        key="unbookMissions"
        disabled={disabled}
      />,
    )
  }

  if (authorization.isAdmin() || authorization.isCityManager()) {
    buttons.push(
      <ButtonWithLabel
        label={fade ? 'Un Fade' : 'Gradual Fade'}
        icon={<CalendarIcon />}
        onClickHandler={onFadeChange}
        key="gradualFade"
        disabled={disabled}
      />,
    )
  }

  return (
    buttons.length > 0 && (
      <React.Fragment>
        <Row className="buttons">{buttons}</Row>
        {showDisableForm && (
          <React.Fragment>
            <Row className={classes.blockingDisclaimer}>
              <Col xs={12}>
                <p>
                  The brand ambassador will be able to complete their remaining
                  booked missions before being blocked
                </p>
              </Col>
            </Row>
            <Row>
              <Col xs={12}>
                <SelectDashboard
                  label="Reason:"
                  input={{
                    name: 'unbookMission',
                    value: blockReasonOption,
                    onChange: handleBlockReasonOptionChange,
                  }}
                  options={options}
                />
              </Col>
            </Row>
            {blockReasonOption === BLOCK_AMBASSADOR_REASON_OTHER && (
              <Row className="buttons">
                <Col xs={12}>
                  <Input
                    label="Specific reason:"
                    input={{
                      value: blockCustomText,
                      onChange: handleBlockCustomTextChange,
                    }}
                    meta={{}}
                  />
                </Col>
              </Row>
            )}
            <Row className="buttons">
              <Col xs={12} className={classes.checkboxWrapper}>
                <Checkbox
                  input={{
                    onClick: toggleForceUnbook,
                    checked: forceUnbook,
                  }}
                />
                <p className={classes.checkboxLabel}>
                  Unbook and cancel all missions
                </p>
              </Col>
            </Row>
            <Row className="buttons">
              <Col xs={3}>
                <SubmitButton
                  value="submit"
                  disabled={disabled || isSubmitBlockedFormDisabled}
                  onClick={handleBlockFormSubmit}
                />
              </Col>
            </Row>
          </React.Fragment>
        )}
      </React.Fragment>
    )
  )
}

AmbassadorStatusChange.propTypes = {
  classes: PropTypes.object.isRequired,
  ambassadorStatus: PropTypes.oneOf(Object.values(AMBASSADOR_STATUS_KEY))
    .isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  fade: PropTypes.any,
  onFadeChange: PropTypes.func.isRequired,
}

export default injectStyles(styles)(AmbassadorStatusChange)



// WEBPACK FOOTER //
// ./src/Containers/City/Page/Ambasador/StatusChange/AmbassadorStatusChange.jsx